import React, { createContext, useContext, useState, useCallback } from "react";
import {
  sortearImpostores,
  sortearPergunta,
  embaralharRespostas,
  contarVotos,
} from "../utils/gameUtils";
import { sortearPalavraSecreta, sortearFaccoes } from "../data/palavras";

const GameContext = createContext(null);

export const FASES = {
  HOME: "HOME",
  MODE_SELECT: "MODE_SELECT",
  SETUP: "SETUP",
  ONLINE: "ONLINE",
  NOMES: "NOMES",
  PERGUNTA_JOGADOR: "PERGUNTA_JOGADOR",
  RESPOSTA_JOGADOR: "RESPOSTA_JOGADOR",
  RESULTADOS: "RESULTADOS",
  VOTACAO: "VOTACAO",
  PLACAR_FINAL: "PLACAR_FINAL",
  PALAVRA_REVEAL: "PALAVRA_REVEAL",
  PALAVRA_DISCUSSAO: "PALAVRA_DISCUSSAO",
  FACCAO_REVEAL: "FACCAO_REVEAL",
  FACCAO_DISCUSSAO: "FACCAO_DISCUSSAO",
  NOTA_REVEAL: "NOTA_REVEAL",
  NOTA_DISCUSSAO: "NOTA_DISCUSSAO",
  NOTA_ADIVINHAR: "NOTA_ADIVINHAR",
  NOTA_RESULTADO: "NOTA_RESULTADO",
  MUSICA_DJ: "MUSICA_DJ",
  MUSICA_OUVIR: "MUSICA_OUVIR",
  MUSICA_RESULTADO: "MUSICA_RESULTADO",
  MEGASENHA_REVEAL: "MEGASENHA_REVEAL",
  MEGASENHA_TURNO: "MEGASENHA_TURNO",
  MEGASENHA_RESULTADO: "MEGASENHA_RESULTADO",
};

export function GameProvider({ children }) {
  const [fase, setFase] = useState(FASES.HOME);
  const [numJogadores, setNumJogadores] = useState(4);
  const [modoJogo, setModoJogo] = useState(1);
  const [modoJogoTipo, setModoJogoTipo] = useState("perguntas");
  const [jogadores, setJogadores] = useState([]);
  const [impostoresIdx, setImpostoresIdx] = useState([]);
  const [perguntaAtual, setPerguntaAtual] = useState(null);
  const [jogadorAtualIdx, setJogadorAtualIdx] = useState(0);
  const [respostas, setRespostas] = useState([]);
  const [respostasEmbaralhadas, setRespostasEmbaralhadas] = useState([]);
  const [votos, setVotos] = useState([]);
  const [votanteAtualIdx, setVotanteAtualIdx] = useState(0);
  const [timerAtivo, setTimerAtivo] = useState(false);
  const [tempoRodada, setTempoRodada] = useState(60);
  const [darkMode, setDarkMode] = useState(true);
  const [rodada, setRodada] = useState(1);
  const [palavraAtual, setPalavraAtual] = useState(null);
  const [faccaoAtual, setFaccaoAtual] = useState(null);
  const [faccaoJogadores, setFaccaoJogadores] = useState({});
  const [duplas, setDuplas] = useState([]);
  const [duplaAtualIdx, setDuplaAtualIdx] = useState(0);
  const [notaSecreta, setNotaSecreta] = useState(null);
  const [notaChute, setNotaChute] = useState(null);
  const [notaResultados, setNotaResultados] = useState([]);
  const [musicaDJIdx, setMusicaDJIdx] = useState(0);
  const [musicaRodada, setMusicaRodada] = useState(1);
  const [musicaPlacar, setMusicaPlacar] = useState({});
  const [numRodadas, setNumRodadas] = useState(5); // configurável no setup (músicas)
  const [megaSenhaPalavra, setMegaSenhaPalavra] = useState(null);
  const [megaSenhaDicas, setMegaSenhaDicas] = useState([]);
  const [megaSenhaParIdx, setMegaSenhaParIdx] = useState(0);
  const [megaSenhaPares, setMegaSenhaPares] = useState([]);
  const [megaSenhaAcertou, setMegaSenhaAcertou] = useState(null);
  const [megaSenhaRodadas, setMegaSenhaRodadas] = useState([]);

  const obterModoAleatorio = (n) => {
    const ops = [1]; if (n >= 4) ops.push(2);
    return ops[Math.floor(Math.random() * ops.length)];
  };

  const formarDuplas = (nomes) => {
    const idxs = [...Array(nomes.length).keys()].sort(() => Math.random() - 0.5);
    const pares = [];
    for (let i = 0; i < idxs.length - 1; i += 2)
      pares.push({ dador: idxs[i], adivinhador: idxs[i + 1] });
    return pares;
  };

  const iniciarJogo = useCallback((nomesJogadores) => {
    setJogadores(nomesJogadores);
    setRodada(1);
    setRespostas([]);
    setVotos([]);
    setVotanteAtualIdx(0);
    setJogadorAtualIdx(0);

    if (modoJogoTipo === "perguntas") {
      const m = obterModoAleatorio(nomesJogadores.length);
      setModoJogo(m);
      setImpostoresIdx(sortearImpostores(nomesJogadores.length, m));
      setPerguntaAtual(sortearPergunta());
      setFase(FASES.PERGUNTA_JOGADOR);
    } else if (modoJogoTipo === "palavra-secreta") {
      const m = obterModoAleatorio(nomesJogadores.length);
      setModoJogo(m);
      setImpostoresIdx(sortearImpostores(nomesJogadores.length, m));
      setPalavraAtual(sortearPalavraSecreta());
      setFase(FASES.PALAVRA_REVEAL);
    } else if (modoJogoTipo === "duas-faccoes") {
      const fac = sortearFaccoes();
      setFaccaoAtual(fac);
      const imp = [Math.floor(Math.random() * nomesJogadores.length)];
      setImpostoresIdx(imp);
      const restantes = nomesJogadores.map((_, i) => i).filter(i => !imp.includes(i)).sort(() => Math.random() - 0.5);
      const mapa = {};
      imp.forEach(i => { mapa[i] = "impostor"; });
      restantes.forEach((idx, pos) => { mapa[idx] = pos % 2 === 0 ? "A" : "B"; });
      setFaccaoJogadores(mapa);
      setFase(FASES.FACCAO_REVEAL);
    } else if (modoJogoTipo === "descubra-nota") {
      const pares = formarDuplas(nomesJogadores);
      setDuplas(pares);
      setDuplaAtualIdx(0);
      setNotaSecreta(Math.ceil(Math.random() * 10));
      setNotaResultados([]);
      setNotaChute(null);
      setFase(FASES.NOTA_REVEAL);
    } else if (modoJogoTipo === "adivinhe-musica") {
      const djIdx = Math.floor(Math.random() * nomesJogadores.length);
      setMusicaDJIdx(djIdx);
      setMusicaRodada(1);
      const pl = {}; nomesJogadores.forEach((_, i) => { pl[i] = 0; });
      setMusicaPlacar(pl);
      setFase(FASES.MUSICA_DJ);
    } else if (modoJogoTipo === "mega-senha") {
      const pares = formarDuplas(nomesJogadores);
      setMegaSenhaPares(pares);
      setMegaSenhaParIdx(0);
      setMegaSenhaPalavra(sortearPalavraSecreta().normal);
      setMegaSenhaDicas([]);
      setMegaSenhaAcertou(null);
      setMegaSenhaRodadas([]);
      setFase(FASES.MEGASENHA_REVEAL);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modoJogoTipo]);

  // MODO PERGUNTAS
  const irParaResposta = useCallback(() => setFase(FASES.RESPOSTA_JOGADOR), []);
  const confirmarResposta = useCallback((resposta) => {
    const nova = { nome: jogadores[jogadorAtualIdx], resposta, jogadorIdx: jogadorAtualIdx };
    const todas = [...respostas, nova];
    setRespostas(todas);
    if (jogadorAtualIdx + 1 < jogadores.length) {
      setJogadorAtualIdx(jogadorAtualIdx + 1);
      setFase(FASES.PERGUNTA_JOGADOR);
    } else {
      setRespostasEmbaralhadas(embaralharRespostas(todas));
      setFase(FASES.RESULTADOS);
    }
  }, [jogadores, jogadorAtualIdx, respostas]);

  const iniciarVotacao = useCallback(() => { setVotanteAtualIdx(0); setFase(FASES.VOTACAO); }, []);
  const registrarVoto = useCallback((votadoIdx) => {
    const novosVotos = [...votos, { votanteIdx: votanteAtualIdx, votadoIdx }];
    setVotos(novosVotos);
    if (votanteAtualIdx + 1 < jogadores.length) setVotanteAtualIdx(votanteAtualIdx + 1);
    else setFase(FASES.PLACAR_FINAL);
  }, [votanteAtualIdx, votos, jogadores.length]);

  const novaRodada = useCallback(() => {
    const m = obterModoAleatorio(jogadores.length);
    setModoJogo(m);
    setImpostoresIdx(sortearImpostores(jogadores.length, m));
    setPerguntaAtual(sortearPergunta());
    setJogadorAtualIdx(0); setRespostas([]); setVotos([]); setVotanteAtualIdx(0);
    setRodada(r => r + 1);
    setFase(FASES.PERGUNTA_JOGADOR);
  }, [jogadores.length]);

  // MODO PALAVRA SECRETA
  const avancarRevealPalavra = useCallback(() => {
    if (jogadorAtualIdx + 1 < jogadores.length) setJogadorAtualIdx(jogadorAtualIdx + 1);
    else { setJogadorAtualIdx(0); setFase(FASES.PALAVRA_DISCUSSAO); }
  }, [jogadorAtualIdx, jogadores.length]);
  const finalizarDiscussaoPalavra = useCallback(() => { setVotos([]); setVotanteAtualIdx(0); setFase(FASES.VOTACAO); }, []);
  const novaRodadaPalavra = useCallback(() => {
    const m = obterModoAleatorio(jogadores.length);
    setModoJogo(m);
    setImpostoresIdx(sortearImpostores(jogadores.length, m));
    setPalavraAtual(sortearPalavraSecreta());
    setJogadorAtualIdx(0); setVotos([]); setVotanteAtualIdx(0);
    setRodada(r => r + 1);
    setFase(FASES.PALAVRA_REVEAL);
  }, [jogadores.length]);

  // MODO DUAS FACÇÕES
  const avancarRevealFaccao = useCallback(() => {
    if (jogadorAtualIdx + 1 < jogadores.length) setJogadorAtualIdx(jogadorAtualIdx + 1);
    else { setJogadorAtualIdx(0); setFase(FASES.FACCAO_DISCUSSAO); }
  }, [jogadorAtualIdx, jogadores.length]);
  const finalizarDiscussaoFaccao = useCallback(() => { setVotos([]); setVotanteAtualIdx(0); setFase(FASES.VOTACAO); }, []);

  // MODO DESCUBRA A NOTA
  const avancarRevealNota = useCallback(() => setFase(FASES.NOTA_DISCUSSAO), []);
  const finalizarDiscussaoNota = useCallback(() => setFase(FASES.NOTA_ADIVINHAR), []);
  const registrarChute = useCallback((chute) => {
    setNotaChute(chute);
    const novoRes = [...notaResultados, {
      duplaIdx: duplaAtualIdx,
      dador: duplas[duplaAtualIdx]?.dador,
      adivinhador: duplas[duplaAtualIdx]?.adivinhador,
      nota: notaSecreta, chute, acertou: chute === notaSecreta,
    }];
    setNotaResultados(novoRes);
    setFase(FASES.NOTA_RESULTADO);
  }, [notaSecreta, notaResultados, duplaAtualIdx, duplas]);
  const proximaDuplaNota = useCallback(() => {
    if (duplaAtualIdx + 1 < duplas.length) {
      setDuplaAtualIdx(duplaAtualIdx + 1);
      setNotaSecreta(Math.ceil(Math.random() * 10));
      setNotaChute(null);
      setFase(FASES.NOTA_REVEAL);
    } else setFase(FASES.PLACAR_FINAL);
  }, [duplaAtualIdx, duplas.length]);

  // MODO ADIVINHE A MÚSICA
  const musicaAcertou = useCallback((vencedorIdx) => {
    setMusicaPlacar(prev => ({ ...prev, [vencedorIdx]: (prev[vencedorIdx] || 0) + 1 }));
    setFase(FASES.MUSICA_RESULTADO);
  }, []);
  const ninguemAcertouMusica = useCallback(() => setFase(FASES.MUSICA_RESULTADO), []);
  const proximaRodadaMusica = useCallback(() => {
    setMusicaDJIdx((musicaDJIdx + 1) % jogadores.length);
    setMusicaRodada(r => r + 1);
    setFase(FASES.MUSICA_DJ);
  }, [musicaDJIdx, jogadores.length]);
  const finalizarMusica = useCallback(() => setFase(FASES.PLACAR_FINAL), []);

  // MODO MEGA SENHA
  const megaSenhaAdicionarDicaEAvancar = useCallback((dica) => {
    const novaDica = { parIdx: megaSenhaParIdx, par: megaSenhaPares[megaSenhaParIdx], dica };
    setMegaSenhaDicas(prev => [...prev, novaDica]);
    const proximo = (megaSenhaParIdx + 1) % megaSenhaPares.length;
    setMegaSenhaParIdx(proximo);
    setFase(FASES.MEGASENHA_TURNO);
  }, [megaSenhaParIdx, megaSenhaPares]);
  const megaSenhaAcertouFn = useCallback(() => {
    setMegaSenhaAcertou(megaSenhaParIdx);
    setMegaSenhaRodadas(prev => [...prev, { palavra: megaSenhaPalavra, vencedor: megaSenhaParIdx, dicas: megaSenhaDicas }]);
    setFase(FASES.MEGASENHA_RESULTADO);
  }, [megaSenhaParIdx, megaSenhaPalavra, megaSenhaDicas]);
  const megaSenhaProximaRodada = useCallback(() => {
    setMegaSenhaPalavra(sortearPalavraSecreta().normal);
    setMegaSenhaDicas([]); setMegaSenhaParIdx(0); setMegaSenhaAcertou(null);
    setFase(FASES.MEGASENHA_REVEAL);
  }, []);
  const megaSenhaFinalizar = useCallback(() => setFase(FASES.PLACAR_FINAL), []);

  // NAV
  const irParaSetup = useCallback(() => setFase(FASES.SETUP), []);
  const irParaOnline = useCallback(() => setFase(FASES.ONLINE), []);
  const irParaHome = useCallback(() => setFase(FASES.HOME), []);
  const irParaModeSelect = useCallback(() => setFase(FASES.MODE_SELECT), []);
  const irParaSetupComModo = useCallback((tipo) => { setModoJogoTipo(tipo); setFase(FASES.SETUP); }, []);

  const reiniciarJogo = useCallback(() => {
    setFase(FASES.HOME);
    setJogadores([]); setImpostoresIdx([]); setPerguntaAtual(null); setPalavraAtual(null);
    setFaccaoAtual(null); setJogadorAtualIdx(0); setRespostas([]); setVotos([]);
    setVotanteAtualIdx(0); setRodada(1); setDuplas([]); setDuplaAtualIdx(0);
    setNotaResultados([]); setMegaSenhaRodadas([]); setMusicaPlacar({}); setMusicaRodada(1);
  }, []);

  const resultadoVotos = contarVotos(votos);

  const value = {
    fase, setFase, numJogadores, setNumJogadores, modoJogo, setModoJogo,
    modoJogoTipo, setModoJogoTipo, jogadores, impostoresIdx, perguntaAtual,
    jogadorAtualIdx, respostas, respostasEmbaralhadas, votos, votanteAtualIdx,
    resultadoVotos, timerAtivo, setTimerAtivo, tempoRodada, setTempoRodada,
    darkMode, setDarkMode, rodada,
    palavraAtual, avancarRevealPalavra, finalizarDiscussaoPalavra, novaRodadaPalavra,
    faccaoAtual, faccaoJogadores, avancarRevealFaccao, finalizarDiscussaoFaccao,
    duplas, duplaAtualIdx, notaSecreta, notaChute, notaResultados,
    avancarRevealNota, finalizarDiscussaoNota, registrarChute, proximaDuplaNota,
    musicaDJIdx, musicaRodada, musicaPlacar, musicaAcertou, ninguemAcertouMusica,
    proximaRodadaMusica, finalizarMusica, numRodadas, setNumRodadas,
    megaSenhaPalavra, megaSenhaDicas, megaSenhaParIdx, megaSenhaPares,
    megaSenhaAcertou, megaSenhaRodadas, megaSenhaAdicionarDicaEAvancar,
    megaSenhaAcertouFn, megaSenhaProximaRodada, megaSenhaFinalizar,
    irParaSetup, irParaOnline, irParaHome, irParaModeSelect, irParaSetupComModo,
    iniciarJogo, irParaResposta, confirmarResposta, iniciarVotacao, registrarVoto,
    novaRodada, reiniciarJogo,
  };

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

export function useGame() {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error("useGame must be used within GameProvider");
  return ctx;
}
